import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { httpClient } from '@/services/http-client';

// ============================================================================
// ============================================================================

export type NotificationEventType =
  | 'content.generation_started'
  | 'content.generation_completed'
  | 'content.generation_failed'
  | 'content.style_generation_started'
  | 'content.style_generation_completed'
  | 'content.style_generation_failed'
  | 'tree.creation_started'
  | 'tree.creation_completed'
  | 'tree.creation_failed'
  | 'batch.started'
  | 'batch.completed'
  | 'batch.failed'
  | 'notification.info'
  | 'notification.success'
  | 'notification.warning'
  | 'notification.error';

export type ProgressEventType = 'progress.update' | 'batch.node_completed';

export type UserEventType = NotificationEventType | ProgressEventType;

export interface NotificationEvent {
  id: string;
  userId: string;
  type: NotificationEventType;
  timestamp: string;
  read: boolean;
  data: Record<string, unknown>;
}

export interface ProgressEvent {
  id: string;
  userId: string;
  type: ProgressEventType;
  resourceType: 'topic_node' | 'learning_tree' | 'batch';
  resourceId: string;
  timestamp: string;
  data: Record<string, unknown>;
}

export type UserEvent = NotificationEvent | ProgressEvent;

// ============================================================================
// ============================================================================

interface UserEventContextType {
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;

  notifications: NotificationEvent[];
  unreadCount: number;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;

  getProgress: (resourceId: string) => ProgressEvent | undefined;
  progressEvents: Map<string, ProgressEvent>;
}

const UserEventContext = createContext<UserEventContextType | null>(null);

// ============================================================================
// ============================================================================

const NOTIFICATION_EVENT_TYPES: NotificationEventType[] = [
  'content.generation_started',
  'content.generation_completed',
  'content.generation_failed',
  'content.style_generation_started',
  'content.style_generation_completed',
  'content.style_generation_failed',
  'tree.creation_started',
  'tree.creation_completed',
  'tree.creation_failed',
  'batch.started',
  'batch.completed',
  'batch.failed',
  'notification.info',
  'notification.success',
  'notification.warning',
  'notification.error',
];

const PROGRESS_EVENT_TYPES: ProgressEventType[] = [
  'progress.update',
  'batch.node_completed',
];

const ALL_EVENT_TYPES = [...NOTIFICATION_EVENT_TYPES, ...PROGRESS_EVENT_TYPES];

// ============================================================================
// ============================================================================

interface UserEventProviderProps {
  children: ReactNode;
}

export function UserEventProvider({ children }: UserEventProviderProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const [progressEvents, setProgressEvents] = useState<Map<string, ProgressEvent>>(
    new Map()
  );

  // Refs
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   */
  const isNotificationEvent = (type: string): type is NotificationEventType => {
    return NOTIFICATION_EVENT_TYPES.includes(type as NotificationEventType);
  };

  /**
   */
  const isProgressEvent = (type: string): type is ProgressEventType => {
    return PROGRESS_EVENT_TYPES.includes(type as ProgressEventType);
  };

  /**
   */
  const connect = useCallback(() => {
    const accessToken = useAuthStore.getState().accessToken;

    if (!accessToken) {
      console.warn('[UserEvents] No access token available');
      return;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const streamUrl = `/api/v1/users/me/events/stream?token=${encodeURIComponent(accessToken)}`;
    console.log('[UserEvents] Connecting to event stream...');

    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('connected', () => {
      console.log('[UserEvents] Connected to event stream');
      setIsConnected(true);
      setError(null);
    });

    ALL_EVENT_TYPES.forEach((type) => {
      eventSource.addEventListener(type, (e: MessageEvent) => {
        try {
          const eventData = JSON.parse(e.data);

          if (isNotificationEvent(type)) {
            console.group(`🔔 [UserEvents] notification: ${type}`);
            console.log('Event ID:', eventData.id);
            console.log('Timestamp:', eventData.timestamp);
            console.log('Data:', eventData.data);
            console.groupEnd();

            setNotifications((prev) => {
              const exists = prev.some((n) => n.id === eventData.id);
              if (exists) return prev;
              return [eventData, ...prev].slice(0, 100);
            });
          } else if (isProgressEvent(type)) {
            console.group(`📊 [UserEvents] progress: ${type}`);
            console.log('Resource:', eventData.resourceType, eventData.resourceId);
            console.log('Progress:', eventData.data?.progress ?? 'N/A', '%');
            console.log('Status:', eventData.data?.status ?? 'N/A');
            console.log('Data:', eventData.data);
            console.groupEnd();

            setProgressEvents((prev) => {
              const newMap = new Map(prev);
              newMap.set(eventData.resourceId, eventData);
              return newMap;
            });
          }
        } catch (err) {
          console.error('[UserEvents] Failed to parse event:', err);
        }
      });
    });

    eventSource.addEventListener('error', (e: Event) => {
      const messageEvent = e as MessageEvent;
      if (messageEvent.data) {
        try {
          const errorData = JSON.parse(messageEvent.data);
          console.error('[UserEvents] Server error:', errorData.error);
          setError(errorData.error);
          setIsConnected(false);
          eventSource.close();
        } catch (err) {
        }
      }
    });

    eventSource.onerror = () => {
      console.warn('[UserEvents] Connection error or closed');
      setIsConnected(false);

      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('[UserEvents] Attempting to reconnect in 5 seconds...');
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 5000);
      }
    };
  }, []);

  /**
   */
  const reconnect = useCallback(() => {
    console.log('[UserEvents] Manual reconnect requested');
    setError(null);
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    connect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   */
  const getProgress = useCallback(
    (resourceId: string) => {
      return progressEvents.get(resourceId);
    },
    [progressEvents]
  );

  /**
   */
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await httpClient.put<void>(`/api/v1/users/me/events/${notificationId}/read`);

        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n
          )
        );
      } catch (err) {
        console.error('[UserEvents] Failed to mark as read:', err);
      }
    },
    []
  );

  /**
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await httpClient.put<void>('/api/v1/users/me/events/mark-all-read');

      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read: true }))
      );
    } catch (err) {
      console.error('[UserEvents] Failed to mark all as read:', err);
    }
  }, []);

  /**
   */
  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    if (!isAuthenticated) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
      setNotifications([]);
      setProgressEvents(new Map());
      return;
    }

    connect();

    return () => {
      console.log('[UserEvents] Cleaning up connection');
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      setProgressEvents((prev) => {
        const newMap = new Map(prev);
        newMap.forEach((event, key) => {
          if (new Date(event.timestamp).getTime() < fiveMinutesAgo) {
            newMap.delete(key);
          }
        });
        return newMap;
      });
    }, 5 * 60 * 1000);

    return () => clearInterval(cleanupInterval);
  }, []);

  const value: UserEventContextType = {
    isConnected,
    error,
    reconnect,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    getProgress,
    progressEvents,
  };

  return (
    <UserEventContext.Provider value={value}>
      {children}
    </UserEventContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 */
export function useUserEvents() {
  const context = useContext(UserEventContext);
  if (!context) {
    throw new Error('useUserEvents must be used within a UserEventProvider');
  }
  return context;
}

/**
 */
export function useProgress(resourceId: string | null) {
  const { getProgress, progressEvents } = useUserEvents();

  if (!resourceId) {
    return { progress: 0, status: 'pending' };
  }

  const event = getProgress(resourceId);

  return {
    progress: event?.data?.progress ?? 0,
    status: event?.data?.status ?? 'pending',
    message: event?.data?.message,
    step: event?.data?.step,
  };
}

/**
 */
export function useNotifications() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useUserEvents();

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
  };
}

/**
 */
export function useNotificationCount() {
  const { unreadCount } = useUserEvents();
  return unreadCount;
}
